// 09-10 (maqueta aprobada «aviso de sin conexión»): un aviso discreto arriba cuando la oficina no recibe nada —
// el móvil sin red, o la conexión en vivo (socket) caída más de 2 s. Antes no se notaba: la oficina se quedaba
// quieta y parecía que los agentes no hacían nada. Un corte breve (cambio de antena, reconexión) no parpadea.
// Volver a ponerse al día (rehidratar la oficina y el chat abierto) lo hace useSocketOficina al reconectar y al
// volver a la app; aquí solo se avisa. El compositor sigue escribible (cursor-ux-mirror §2.1).
import { WifiOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { t } from '../../i18n';
import { useAppStore } from '../../stores/app.store';
import { MARGEN_PX, useIzquierdaPx } from './marco/Escenario';

export const ESPERA_AVISO_MS = 2_000;

/** true cuando hay que avisar: sin red (en el acto) o el socket lleva más de ESPERA_AVISO_MS sin conexión. */
export function useSinConexion(): boolean {
  const conectado = useAppStore((s) => s.connectionStatus === 'connected');
  const [enLinea, setEnLinea] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  const [caidoHaRato, setCaidoHaRato] = useState(false);
  // Revisión 09-10: el reloj de 2 s corría con la app en segundo plano (iOS corta el socket al dormir) y al volver el
  // aviso salía de golpe durante la reconexión. Cada vuelta a la app vuelve a dar sus 2 s de gracia.
  const [vuelta, setVuelta] = useState(0);
  useEffect(() => {
    const alVolver = () => document.visibilityState === 'visible' && setVuelta((v) => v + 1);
    document.addEventListener('visibilitychange', alVolver);
    return () => document.removeEventListener('visibilitychange', alVolver);
  }, []);

  useEffect(() => {
    const cambia = () => setEnLinea(navigator.onLine !== false);
    window.addEventListener('online', cambia);
    window.addEventListener('offline', cambia);
    return () => {
      window.removeEventListener('online', cambia);
      window.removeEventListener('offline', cambia);
    };
  }, []);

  useEffect(() => {
    if (conectado) return;
    const id = setTimeout(() => setCaidoHaRato(true), ESPERA_AVISO_MS);
    // Al volver la conexión se olvida: el próximo corte vuelve a esperar sus 2 s antes de avisar.
    return () => {
      clearTimeout(id);
      setCaidoHaRato(false);
    };
  }, [conectado, vuelta]);

  return !enLinea || (!conectado && caidoHaRato);
}

/** `movil`: en su sitio, encima de la lista o del chat (flotando tapaba el título y «Contratar»); si no, flota arriba,
 *  centrado en el hueco de las ventanas (a la derecha de la lista): centrado en toda la pantalla, en un portátil tapaba
 *  el título de la ventana abierta («Sala de Control», 10-10), que empieza a la izquierda de ese hueco. */
export function AvisoSinConexion({ movil = false }: { movil?: boolean }) {
  const sinConexion = useSinConexion();
  const izquierda = useIzquierdaPx();
  if (!sinConexion) return null;
  return (
    <div
      className={movil ? 'flex shrink-0 justify-center px-4 pt-2' : 'pointer-events-none fixed top-[calc(0.5rem+env(safe-area-inset-top))] z-[60] flex justify-center px-4'}
      style={movil ? undefined : { left: izquierda, right: MARGEN_PX }}
    >
      <p
        role="status"
        className="flex items-center gap-2 rounded-full bg-oficina-elevada px-3 py-1.5 text-o-sub text-oficina-texto2 shadow-o-flota ring-1 ring-white/10 animate-in fade-in-0"
      >
        <WifiOff className="h-3.5 w-3.5 text-oficina-bloqueado" aria-hidden />
        {t('oficinaV2.sinConexion')}
      </p>
    </div>
  );
}
